import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommentDto, WorkOrderDetailsDto } from '@app/generated/model';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '@app/core/services/user.service';
import { FilterOption } from '@app/work-order/comments/filter-option';
import { AddEditCommentModel, CommentContext } from '@app/work-order/comments/add-comment.model';
import { AddCommentComponent } from '@app/work-order/comments/add-comment.component';
import { WorkOrderCommentsService } from '@app/work-order/work-order-comments.service';
import { LoggingService } from '@app/shared/services/logging.service';
import { Subject, takeUntil } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CommentComponent implements OnInit, OnDestroy {
  @Input() comment: CommentDto;
  @Input() workOrder: WorkOrderDetailsDto;

  currentUsername = '';
  isAdmin = false;

  showReplyInput = false;
  replyText = '';
  replyLoading = false;
  repliesExpanded = true;

  private destroy$ = new Subject<void>();

  constructor(
    private workOrderCommentsService: WorkOrderCommentsService,
    private userService: UserService,
    private loggingService: LoggingService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userService
      .getObserver()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUsername = user?.username ?? '';
        this.isAdmin = user?.admin === true;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getReferenceTypeKey(item: CommentDto | FilterOption): string {
    return `workOrderComments.referenceType.${item.referenceType}`;
  }

  getReferenceLabel(item: CommentDto | FilterOption): string {
    if (!this.workOrder) return '';
    if (item.referenceType === 'WORK_ORDER') return this.workOrder.workOrderNumber;
    if (item.referenceName) return item.referenceName;
    if (item.referenceId) return item.referenceId;
    return '';
  }

  // Mirrors the backend rule: only the author or an admin may edit/delete a comment.
  canModify(item: CommentDto): boolean {
    return (
      this.isAdmin || (!!this.currentUsername && item.createdBy?.toLowerCase() === this.currentUsername.toLowerCase())
    );
  }

  editComment(): void {
    this.openEditDialog(this.comment);
  }

  editReply(reply: CommentDto): void {
    this.openEditDialog(reply);
  }

  private openEditDialog(target: CommentDto): void {
    const context: CommentContext = {
      referenceType: target.referenceType,
      referenceId: target.referenceId,
      referenceName: target.referenceName,
    };
    const addData: AddEditCommentModel = { context, comment: target };
    this.dialog.open(AddCommentComponent, {
      width: '90vw',
      maxWidth: '810px',
      position: { top: '10%', left: '10%' },
      panelClass: 'dwo-dialog',
      data: addData,
    });
  }

  get replies(): CommentDto[] {
    return Array.isArray(this.comment.replies) ? (this.comment.replies as CommentDto[]) : [];
  }

  get replyCount(): number {
    return this.replies.length;
  }

  toggleReplies(): void {
    this.repliesExpanded = !this.repliesExpanded;
  }

  openReplyInput(): void {
    this.showReplyInput = true;
  }

  cancelReply(): void {
    this.showReplyInput = false;
    this.replyText = '';
  }

  onReplyKeydown(event: Event): void {
    const ke = event as KeyboardEvent;
    if (!ke.shiftKey) {
      event.preventDefault();
      this.submitReply();
    }
  }

  async submitReply(): Promise<void> {
    if (!this.replyText?.trim()) return;
    this.replyLoading = true;
    try {
      await this.workOrderCommentsService.addReply({
        parentId: this.comment.id,
        referenceId: this.comment.referenceId,
        referenceName: this.comment.referenceName,
        referenceType: this.comment.referenceType,
        text: this.replyText.trim(),
      });
      this.replyText = '';
      this.showReplyInput = false;
      this.repliesExpanded = true;
    } catch (err) {
      this.loggingService.error(err);
    } finally {
      this.replyLoading = false;
    }
  }

  private static readonly AVATAR_COLORS = [
    '#1877f2',
    '#e4405f',
    '#00b09b',
    '#f7971e',
    '#8e44ad',
    '#2980b9',
    '#27ae60',
    '#d35400',
  ];

  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(/[.\s_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('');
  }

  getAvatarColor(name: string): string {
    if (!name) return '#aaa';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return CommentComponent.AVATAR_COLORS[Math.abs(hash) % CommentComponent.AVATAR_COLORS.length];
  }
}
